import { Box, Card, Typography } from '@mui/material';
import { format } from 'date-fns/esm';

import useI18n from 'hooks/useI18n';
import { inputInformationWidth } from '../InputInformation';

const ICON_SRC = `${process.env.PUBLIC_URL ?? ''}/patient-select-hint-icon.png`;

interface Props {
  contentBoxProps: {
    display: string;
    columnGap?: number;
    overflow: string;
    height: string;
  };
  coachRef?: any;
}

const PatientSelectPlaceholder = ({ contentBoxProps, coachRef }: Props) => {
  const i18n = useI18n();
  const today = format(new Date(), 'yyyy-MM-dd');

  return (
    <Box {...contentBoxProps} position="relative">
      <Box
        aria-hidden
        sx={{
          display: 'flex',
          columnGap: 3,
          width: '100%',
          height: '100%',
          opacity: 0.58,
          filter: 'blur(1.6px)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <Box
          flex={1.2}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Card
            component="section"
            sx={{
              p: '20px 15px',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography variant="subtitle2" fontSize={13} mb={1}>
              Hồ sơ kê đơn
            </Typography>
            <Typography
              variant="caption"
              component="p"
              color="#00000080"
              mb={1}
            >
              Ngày giờ kê đơn {today}
            </Typography>
            <Typography
              variant="body2"
              lineHeight="22px"
              sx={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                py: 0.7,
              }}
            >
              Aspirin 100mg 1TAB (QD PC)
            </Typography>
            <Typography
              variant="body2"
              lineHeight="22px"
              sx={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                py: 0.7,
              }}
            >
              Amlodipine 5mg 1TAB (QD PC)
            </Typography>
            <Typography variant="body2" lineHeight="22px" sx={{ py: 0.7 }}>
              Nitroglycerin ngậm dưới lưỡi (PRN khi đau ngực)
            </Typography>
          </Card>
        </Box>

        <Box
          flex={1.2}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          ref={coachRef}
        >
          <Card
            component="section"
            sx={{
              p: '10px 15px',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography variant="subtitle2" fontSize={13} mb={1}>
              Lịch sử điều dưỡng
            </Typography>
            <Typography variant="caption" fontWeight={700}>
              NANDA 14117041 (Domain) RN {today} 08:41
            </Typography>
            <Typography
              variant="caption"
              display="block"
              mt={1}
              sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)', pt: 1.2 }}
            >
              • Domain | 1. Nâng cao sức khỏe
            </Typography>
            <Typography
              variant="caption"
              display="block"
              mt={1}
              sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)', pt: 1.2 }}
            >
              • Class | 1. Nhận thức sức khỏe
            </Typography>
            <Typography
              variant="caption"
              display="block"
              mt={1}
              sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)', pt: 1.2 }}
            >
              • Chẩn đoán | Giảm tham gia hoạt động giải trí
            </Typography>
          </Card>
        </Box>

        <Box
          flex={1.2}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          width={inputInformationWidth}
        >
          <Card
            component="section"
            sx={{ p: '12px 14px', mb: 1.5, flex: '0 0 auto' }}
          >
            <Typography variant="subtitle2" fontSize={13} mb={0.5}>
              Ghi chú bệnh nhân
            </Typography>
            <Typography variant="caption" color="#0000008e">
              Có thể nhập ghi chú và tóm tắt sau khi chọn bệnh nhân.
            </Typography>
          </Card>
          <Card component="section" sx={{ p: '12px 14px', flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle2" fontSize={13} mb={0.5}>
              Nhập hồ sơ điều dưỡng
            </Typography>
            <Typography variant="caption" color="#0000008e" display="block">
              Các biểu mẫu NANDA, SOAPIE sẽ được kích hoạt tại đây.
            </Typography>
          </Card>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            width: 367,
            borderRadius: '10px',
            padding: '30px 50px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            component="img"
            src={ICON_SRC}
            alt=""
            sx={{
              width: 100,
              height: 100,
              objectFit: 'contain',
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
              {i18n('PATIENT.SELECT.EMPTY.TITLE')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(0, 0, 0, 0.9)', fontSize: 13, display: 'block' }}
            >
              {i18n('PATIENT.SELECT.EMPTY.DESCRIPTION')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PatientSelectPlaceholder;
